import React, { useState } from 'react';
import { useCalculations } from '../../context/CalculationContext';
import { useScenarios } from '../../context/ScenarioContext';
import { useSettings } from '../../context/SettingsContext'; // To check NPV setting
import { Popover, Typography, Slider, Box, IconButton, Switch, FormControlLabel } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

// Helper to format currency
const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

function KeyMetricsSummary() {
  const { calculationResults, isCalculating } = useCalculations();
  const { scenarios } = useScenarios();
  const { globalSettings, handleGlobalSettingChange } = useSettings();
  
  // State for Popover control
  const [npvAnchorEl, setNpvAnchorEl] = useState(null);
  const [tempDiscountRate, setTempDiscountRate] = useState(globalSettings.discountRate);

  const visibleScenarios = scenarios.filter(s => s.visible !== false); // Default to visible if undefined

  // Function to extract key metrics for a scenario
  const getMetrics = (scenarioId) => {
    const results = calculationResults.scenarioResults[scenarioId];
    const scenario = scenarios.find(s => s.id === scenarioId);

    if (!results || results.error || !scenario) {
      return { name: scenario?.name || `Scenario ${scenarioId}`, error: true };
    }

    const { years, pensionData, tspBalanceData: tspData, npvData, high3Salary } = results; // Renamed tspBalanceData to tspData for local use
    
    // Add safety checks for required arrays
    if (!years || !Array.isArray(years) || !pensionData || !Array.isArray(pensionData)) {
      return { name: scenario?.name || `Scenario ${scenarioId}`, error: true };
    }
    
    const retirementYear = scenario.retirementYear;
    const retirementIndex = years.indexOf(retirementYear);

    // Find first non-zero pension year/value
    let firstPensionYear = 'N/A';
    let firstPensionAmount = 0;
    if (retirementIndex !== -1) {
        for (let i = retirementIndex; i < pensionData.length; i++) {
            if (pensionData[i] > 0) {
                firstPensionYear = years[i];
                firstPensionAmount = pensionData[i];
                break;
            }
        }
         // If still 0, maybe it's deferred starting later
         if (firstPensionAmount === 0 && scenario.pensionType === 'deferred') {
             const deferredStartYear = new Date(scenario.dateOfBirth).getFullYear() + scenario.deferredRetirementAge;
             const deferredIndex = years.indexOf(deferredStartYear);
             if(deferredIndex !== -1 && pensionData[deferredIndex] > 0){
                 firstPensionYear = years[deferredIndex];
                 firstPensionAmount = pensionData[deferredIndex];
             }
         }
    }


    // Get TSP balance at retirement instead of final balance
    const tspBalanceAtRetirement = retirementIndex !== -1 && tspData && Array.isArray(tspData) ? tspData[retirementIndex] ?? 0 : 0;
    // Get NPV of pension only, not the total NPV
    const pensionNpv = npvData?.pensionNpv?.reduce((sum, value) => sum + value, 0) ?? 0;
    const totalAdditionalIncomeNpv = npvData?.additionalIncomeNpv?.reduce((sum, value) => sum + value, 0) ?? 0;

    return {
      name: scenario.name,
      color: scenario.color,
      high3: high3Salary,
      pensionStartYear: firstPensionYear,
      firstPension: firstPensionAmount,
      finalTsp: tspBalanceAtRetirement, // Still using finalTsp as the property name to avoid breaking changes
      pensionNpv: pensionNpv, // Changed from totalNpv to pensionNpv
      totalAdditionalIncomeNpv: totalAdditionalIncomeNpv, // New: NPV of Additional Income
      error: false
    };
  };

  // Handle popover close
  const handlePopoverClose = () => {
    setNpvAnchorEl(null);
  };

  // Handle discount rate change
  const handleDiscountRateChange = (event, newValue) => {
    setTempDiscountRate(newValue);
  };

  // Apply the discount rate change
  const applyDiscountRate = () => {
    handleGlobalSettingChange('discountRate', tempDiscountRate);
    handlePopoverClose();
  };

  // Calculate if popover is open
  const isNpvPopoverOpen = Boolean(npvAnchorEl);
  const npvPopoverId = isNpvPopoverOpen ? 'npv-popover' : undefined;

  // First return removed, only keeping the second complete return statement

  return (
    <>
      <div className="key-metrics-summary">
        <h4>Key Metrics Summary</h4>
        {isCalculating && <p style={{textAlign: 'center', fontStyle: 'italic', color: '#6c757d'}}>Calculating...</p>}
        {!isCalculating && visibleScenarios.length === 0 && <p style={{textAlign: 'center', fontStyle: 'italic', color: '#6c757d'}}>No visible scenarios to summarize.</p>}
        {!isCalculating && visibleScenarios.length > 0 && (
            <div className="metrics-grid">
            {visibleScenarios.map(scenario => {
              const metrics = getMetrics(scenario.id);
              return (
                <div key={scenario.id} className="metric-scenario" style={{ borderLeftColor: metrics.color }}>
                  <h5>{metrics.name}</h5>
                  {metrics.error ? (
                    <p className="error-message">Calculation Error</p>
                  ) : (
                    <>
                      <div className="metric-item">
                        <span className="metric-label">High-3 Salary:</span>
                        <span className="metric-value">{formatCurrency(metrics.high3)}</span>
                      </div>
                       <div className="metric-item">
                        <span className="metric-label">First Pension ({metrics.pensionStartYear}):</span>
                        <span className="metric-value">{formatCurrency(metrics.firstPension)}</span>
                      </div>
                      {globalSettings.calculateNpv && (
                         <div className="metric-item">
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span className="metric-label">NPV of Pension:</span>
                            <span className="metric-value" style={{ marginLeft: '4px', marginRight: '4px' }}>
                              {formatCurrency(metrics.pensionNpv)}
                            </span>
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                setNpvAnchorEl(e.currentTarget);
                                setTempDiscountRate(globalSettings.discountRate);
                              }}
                              style={{ padding: '2px' }}
                            >
                              <InfoIcon fontSize="small" color="primary" />
                            </IconButton>
                          </div>
                        </div>
                      )}
                      {globalSettings.calculateNpv && metrics.totalAdditionalIncomeNpv > 0 && (
                        <div className="metric-item">
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span className="metric-label">NPV of Addl. Income:</span>
                            <span className="metric-value" style={{ marginLeft: '4px', marginRight: '4px' }}>
                              {formatCurrency(metrics.totalAdditionalIncomeNpv)}
                            </span>
                            {/* No InfoIcon needed here as it's covered by the main NPV popover */}
                          </div>
                        </div>
                      )}
                       <div className="metric-item">
                        <span className="metric-label">TSP Balance at Retirement:</span>
                        <span className="metric-value">{formatCurrency(metrics.finalTsp)}</span>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* NPV Popover */}
      <Popover
        id={npvPopoverId}
        open={isNpvPopoverOpen}
        anchorEl={npvAnchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Net Present Value (NPV)</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            NPV shows the current value of all future pension payments. It discounts future money based on the principle that money today is worth more than the same amount in the future.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The standard discount rate used in financial planning is typically between 2-5%. A higher discount rate reduces the NPV, reflecting a higher preference for money today versus in the future.
          </Typography>
          
          <FormControlLabel
            control={
              <Switch 
                checked={globalSettings.calculateNpv}
                onChange={(e) => handleGlobalSettingChange('calculateNpv', e.target.checked)}
                size="small"
              />
            }
            label="Calculate Net Present Value"
            sx={{ mb: 2 }}
          />
          
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Discount Rate: {tempDiscountRate}%
          </Typography>
          <Slider
            value={tempDiscountRate}
            onChange={handleDiscountRateChange}
            aria-labelledby="discount-rate-slider"
            step={0.1}
            marks
            min={0}
            max={10}
            valueLabelDisplay="auto"
            disabled={!globalSettings.calculateNpv}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button 
              onClick={applyDiscountRate} 
              style={{
                padding: '8px 16px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
              disabled={!globalSettings.calculateNpv}
            >
              Apply
            </button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}

export default KeyMetricsSummary;